import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, IconButton, Select, MenuItem, Button,
  TextField, Switch, FormControlLabel, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Tooltip, AppBar, Toolbar
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';

function useAdminFetch<T>(url: string, deps: any[] = []) {
  const { token } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) setData(json.data);
      else setError(json.error);
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }, [url, token]);

  useEffect(() => { load(); }, deps);
  return { data, loading, error, reload: load };
}

// ── Users Tab ──────────────────────────────────────────────────────────────
function UsersTab() {
  const { token, user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; email: string } | null>(null);
  const { data, loading, reload } = useAdminFetch<any[]>(`/api/admin/users?page=${page}&limit=20&search=${search}`, [page, search]);

  const updateRole = async (id: string, role: string) => {
    await fetch(`/api/admin/users/${id}/role`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ role }),
    });
    reload();
  };

  const toggleStatus = async (id: string, isActive: boolean) => {
    await fetch(`/api/admin/users/${id}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ isActive }),
    });
    reload();
  };

  const deleteUser = async (id: string) => {
    await fetch(`/api/admin/users/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    setConfirmDelete(null);
    reload();
  };

  const verifyUser = async (id: string) => {
    await fetch(`/api/admin/users/${id}/verify`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    reload();
  };

  return (
    <Box>
      <TextField size="small" placeholder="搜尋 email / 名稱" value={search}
        onChange={e => { setSearch(e.target.value); setPage(1); }} sx={{ mb: 2, width: 280 }} />
      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell>角色</TableCell>
              <TableCell>Email 驗證</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>建立時間</TableCell>
              <TableCell>啟用</TableCell>
              <TableCell>刪除</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data || []).map((u: any) => (
              <TableRow key={u._id}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.displayName || '-'}</TableCell>
                <TableCell>
                  <Select size="small" value={u.role} onChange={e => updateRole(u._id, e.target.value as string)}
                    sx={{ fontSize: 12 }}>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="advanced">Advanced</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <Chip label={u.isActive ? '啟用' : '停用'} color={u.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  {u.emailVerified
                    ? <Chip label="已驗證" color="success" size="small" />
                    : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label="未驗證" color="warning" size="small" />
                        <Tooltip title="手動放行（標記為已驗證）">
                          <Button size="small" variant="outlined" color="warning" sx={{ fontSize: 11, py: 0, px: 0.5, minWidth: 0 }}
                            onClick={() => verifyUser(u._id)}>放行</Button>
                        </Tooltip>
                      </Box>
                  }
                </TableCell>
                <TableCell>{new Date(u.createdAt).toLocaleDateString('zh-TW')}</TableCell>
                <TableCell>
                  <Switch checked={u.isActive} size="small" onChange={e => toggleStatus(u._id, e.target.checked)} />
                </TableCell>
                <TableCell>
                  <Tooltip title={u._id === currentUser?.id ? '不能刪除自己' : '刪除帳號'}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={u._id === currentUser?.id}
                        onClick={() => setConfirmDelete({ id: u._id, email: u.email })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一頁</Button>
        <Typography variant="body2" sx={{ alignSelf: 'center' }}>第 {page} 頁</Typography>
        <Button size="small" disabled={(data || []).length < 20} onClick={() => setPage(p => p + 1)}>下一頁</Button>
      </Box>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>確認刪除帳號</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除帳號 <strong>{confirmDelete?.email}</strong> 嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>取消</Button>
          <Button color="error" variant="contained" onClick={() => confirmDelete && deleteUser(confirmDelete.id)}>
            確認刪除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Audit Logs Tab ─────────────────────────────────────────────────────────
function AuditTab() {
  const [page, setPage] = useState(1);
  const { data, loading } = useAdminFetch<any[]>(`/api/admin/audit-logs?page=${page}&limit=50`, [page]);

  return (
    <Box>
      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>時間</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>動作</TableCell>
              <TableCell>資源</TableCell>
              <TableCell>詳情</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data || []).map((log: any) => (
              <TableRow key={log._id}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                  {new Date(log.createdAt).toLocaleString('zh-TW')}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{log.email || '-'}</TableCell>
                <TableCell><Chip label={log.action} size="small" variant="outlined" /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>{log.resource}</TableCell>
                <TableCell sx={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {log.detail || '-'}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{log.ip || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一頁</Button>
        <Typography variant="body2" sx={{ alignSelf: 'center' }}>第 {page} 頁</Typography>
        <Button size="small" disabled={(data || []).length < 50} onClick={() => setPage(p => p + 1)}>下一頁</Button>
      </Box>
    </Box>
  );
}

// ── Features Tab ───────────────────────────────────────────────────────────
function FeaturesTab() {
  const { token } = useAuth();
  const { data, loading, reload } = useAdminFetch<any[]>('/api/admin/features');
  const allRoles = ['basic', 'standard', 'advanced', 'enterprise', 'admin'];
  const [localData, setLocalData] = useState<any[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    if (data && !localData) setLocalData(JSON.parse(JSON.stringify(data)));
  }, [data]);

  const toggleRole = (flagKey: string, role: string) => {
    if (!localData) return;
    setLocalData(prev => (prev || []).map(f => {
      if (f.key !== flagKey) return f;
      const roles = f.enabledForRoles.includes(role)
        ? f.enabledForRoles.filter((r: string) => r !== role)
        : [...f.enabledForRoles, role];
      return { ...f, enabledForRoles: roles };
    }));
    setDirty(true);
    setSaveMsg('');
  };

  const handleSave = async () => {
    if (!localData) return;
    setSaving(true); setSaveMsg('');
    try {
      for (const flag of localData) {
        await fetch(`/api/admin/features/${flag.key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ enabledForRoles: flag.enabledForRoles }),
        });
      }
      setSaveMsg('Saved');
      setDirty(false);
      reload();
    } catch { setSaveMsg('Save failed'); }
    finally { setSaving(false); }
  };

  const displayData = localData || data || [];

  return (
    <Box>
      {loading ? <CircularProgress /> : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell>Description</TableCell>
                {allRoles.map(r => <TableCell key={r} align="center">{r}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((flag: any) => (
                <TableRow key={flag.key}>
                  <TableCell sx={{ fontWeight: 600 }}>{flag.label}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{flag.description}</TableCell>
                  {allRoles.map(role => (
                    <TableCell key={role} align="center">
                      <Switch checked={flag.enabledForRoles.includes(role)} size="small"
                        onChange={() => toggleRole(flag.key, role)} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {saveMsg && <Typography variant="body2" color={saveMsg === 'Saved' ? 'success.main' : 'error.main'}>{saveMsg}</Typography>}
            {dirty && <Typography variant="caption" color="warning.main">Unsaved changes</Typography>}
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Change Password Tab ────────────────────────────────────────────────────
// ── System Tab ──────────────────────────────────────────────────────────
function SystemTab() {
  const { token } = useAuth();
  const [regEnabled, setRegEnabled] = useState(true);
  const [serverStatus, setServerStatus] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [restartMsg, setRestartMsg] = useState('');

  // Load settings
  useEffect(() => {
    fetch('/api/admin/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setRegEnabled(d.data.registrationEnabled !== false); });
  }, [token]);

  // Load server status
  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/server-status', { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) setServerStatus(d.data);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(loadStatus, 3000);
    return () => clearInterval(iv);
  }, [autoRefresh, loadStatus]);

  const toggleRegistration = async (enabled: boolean) => {
    setRegEnabled(enabled);
    await fetch('/api/admin/settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ registrationEnabled: enabled }),
    });
  };

  const handleRestart = async () => {
    if (!confirm('確定要重啟伺服器嗎？')) return;
    setRestartMsg('伺服器重啟中...');
    try {
      await fetch('/api/admin/restart', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    setTimeout(() => setRestartMsg('伺服器已重啟，請重新整理頁面'), 3000);
  };

  const statusColor = (s: string) => s === 'running' || s === 'connected' ? 'success' : 'error';

  return (
    <Box>
      {/* Registration toggle */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>帳號註冊</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Switch checked={regEnabled} onChange={e => toggleRegistration(e.target.checked)} />
          <Typography variant="body2" color={regEnabled ? 'success.main' : 'error.main'} fontWeight={600}>
            {regEnabled ? '開放註冊' : '關閉註冊'}
          </Typography>
        </Box>
      </Box>

      {/* Server status */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>伺服器狀態</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch size="small" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            <Typography variant="caption" color="text.secondary">自動更新 (3s)</Typography>
            <Button size="small" variant="outlined" onClick={loadStatus}>重新整理</Button>
          </Box>
        </Box>

        {loading ? <CircularProgress size={20} /> : serverStatus && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>服務</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>詳情</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell fontWeight={600}>Backend (Node.js)</TableCell>
                <TableCell><Chip label={serverStatus.backend.status} color={statusColor(serverStatus.backend.status) as any} size="small" /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>PID: {serverStatus.backend.pid} · Uptime: {Math.round(serverStatus.backend.uptime / 60)}min · RAM: {serverStatus.backend.memoryMB}MB</TableCell>
              </TableRow>
              <TableRow>
                <TableCell fontWeight={600}>MongoDB</TableCell>
                <TableCell><Chip label={serverStatus.mongodb.status} color={statusColor(serverStatus.mongodb.status) as any} size="small" /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>Host: {serverStatus.mongodb.host}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell fontWeight={600}>Redis</TableCell>
                <TableCell><Chip label={serverStatus.redis.status} color={statusColor(serverStatus.redis.status) as any} size="small" /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>Cache & Queue</TableCell>
              </TableRow>
              <TableRow>
                <TableCell fontWeight={600}>System</TableCell>
                <TableCell><Chip label="info" color="primary" size="small" /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>{serverStatus.system.platform}/{serverStatus.system.arch} · {serverStatus.system.cpus} CPUs · RAM: {serverStatus.system.freeMemMB}/{serverStatus.system.totalMemMB}MB</TableCell>
              </TableRow>
              {serverStatus.disk && (
              <TableRow>
                <TableCell fontWeight={600}>Disk</TableCell>
                <TableCell><Chip label={parseInt(serverStatus.disk.usePct) > 80 ? 'warning' : 'ok'} color={parseInt(serverStatus.disk.usePct) > 80 ? 'warning' : 'success'} size="small" /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>Used: {serverStatus.disk.used}/{serverStatus.disk.total} ({serverStatus.disk.usePct}) · Free: {serverStatus.disk.avail}</TableCell>
              </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Restart */}
      <Box sx={{ p: 2, border: '1px solid #ffcdd2', borderRadius: 2, bgcolor: '#fff5f5' }}>
        <Typography variant="subtitle1" fontWeight={700} color="error" sx={{ mb: 1 }}>危險操作</Typography>
        {restartMsg && <Alert severity="warning" sx={{ mb: 1 }}>{restartMsg}</Alert>}
        <Button variant="contained" color="error" onClick={handleRestart}>
          重啟伺服器
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          伺服器將在 Docker 中自動重啟（restart: unless-stopped）
        </Typography>
      </Box>
    </Box>
  );
}

// ── Change Password Tab ────────────────────────────────────────────────
function PasswordTab() {
  const { token } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(''); setErr('');
    if (newPassword !== confirm) { setErr('兩次密碼不一致'); return; }
    const res = await fetch('/api/admin/change-password', {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ newPassword }),
    });
    const data = await res.json();
    if (data.success) setMsg('密碼已更新');
    else setErr(data.error);
  };

  return (
    <Box sx={{ maxWidth: 400 }}>
      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField fullWidth label="新密碼" type="password" value={newPassword}
          onChange={e => setNewPassword(e.target.value)} sx={{ mb: 2 }} required inputProps={{ minLength: 6 }} />
        <TextField fullWidth label="確認新密碼" type="password" value={confirm}
          onChange={e => setConfirm(e.target.value)} sx={{ mb: 3 }} required />
        <Button variant="contained" type="submit">更新密碼</Button>
      </form>
    </Box>
  );
}

// ── Proxy Settings Tab ─────────────────────────────────────────────────
function ProxyTab() {
  const { token } = useAuth();
  const [targets, setTargets] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [form, setForm] = useState({ name: '', targetUrl: '', path: '/', enabled: true });
  const [msg, setMsg] = useState('');
  const [checking, setChecking] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(false);

  const loadTargets = async () => {
    try {
      const res = await fetch('/api/proxy/targets', { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) setTargets(d.data);
    } catch {}
    setLoading(false);
  };

  const loadLogs = async () => {
    try {
      const res = await fetch('/api/proxy/logs?limit=30', { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) setLogs(d.data);
    } catch {}
  };

  useEffect(() => { loadTargets(); }, []);

  const handleSave = async () => {
    const method = editTarget ? 'PUT' : 'POST';
    const url = editTarget ? `/api/proxy/targets/${editTarget._id}` : '/api/proxy/targets';
    const res = await fetch(url, {
      method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    const d = await res.json();
    if (d.success) {
      setMsg(editTarget ? '已更新' : '已新增');
      setShowAdd(false); setEditTarget(null);
      setForm({ name: '', targetUrl: '', path: '/', enabled: true });
      loadTargets();
    } else { setMsg(d.error || 'Error'); }
    setTimeout(() => setMsg(''), 3000);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`確定要刪除代理目標「${name}」嗎？`)) return;
    await fetch(`/api/proxy/targets/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    loadTargets();
    setMsg('已刪除');
    setTimeout(() => setMsg(''), 3000);
  };

  const handleCheck = async (id: string) => {
    setChecking(id);
    await fetch(`/api/proxy/targets/${id}/check`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    await loadTargets();
    setChecking(null);
  };

  const handleReload = async () => {
    await fetch('/api/proxy/reload', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setMsg('Nginx 設定已重載');
    setTimeout(() => setMsg(''), 3000);
  };

  const startEdit = (t: any) => {
    setEditTarget(t);
    setForm({ name: t.name, targetUrl: t.targetUrl, path: t.path || '/', enabled: t.enabled });
    setShowAdd(true);
  };

  const riskColor = (r: string) => r === 'low' ? 'success' : r === 'medium' ? 'warning' : r === 'high' ? 'error' : 'default';

  return (
    <Box>
      {msg && <Alert severity="info" sx={{ mb: 2 }}>{msg}</Alert>}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setShowAdd(true); setEditTarget(null); setForm({ name: '', targetUrl: '', path: '/', enabled: true }); }}>
          新增代理目標
        </Button>
        <Button variant="outlined" size="small" onClick={handleReload}>重載 Nginx</Button>
        <Button variant="outlined" size="small" onClick={() => { setShowLogs(!showLogs); if (!showLogs) loadLogs(); }}>
          {showLogs ? '隱藏 Log' : '顯示 Log'}
        </Button>
      </Box>

      {/* Add/Edit Form */}
      {showAdd && (
        <Paper sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{editTarget ? '編輯代理目標' : '新增代理目標'}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" label="名稱" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} sx={{ width: 150 }} />
            <TextField size="small" label="目標 URL" value={form.targetUrl} onChange={e => setForm({ ...form, targetUrl: e.target.value })} sx={{ width: 300 }} placeholder="https://example.com" />
            <TextField size="small" label="路徑" value={form.path} onChange={e => setForm({ ...form, path: e.target.value })} sx={{ width: 100 }} />
            <FormControlLabel control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} size="small" />} label="啟用" />
            <Button variant="contained" size="small" onClick={handleSave}>儲存</Button>
            <Button size="small" onClick={() => { setShowAdd(false); setEditTarget(null); }}>取消</Button>
          </Box>
        </Paper>
      )}

      {/* Targets Table */}
      {loading ? <CircularProgress size={20} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>名稱</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>目標 URL</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>路徑</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>狀態</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>TLS / PQC</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {targets.map((t: any) => (
              <TableRow key={t._id}>
                <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{t.targetUrl}</TableCell>
                <TableCell>{t.path}</TableCell>
                <TableCell>
                  <Chip label={t.enabled ? '啟用' : '停用'} color={t.enabled ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  {t.tlsStatus ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={t.tlsStatus.risk?.toUpperCase()} color={riskColor(t.tlsStatus.risk) as any} size="small" />
                      <Typography variant="caption" sx={{ fontSize: 11 }}>
                        {t.tlsStatus.tlsVersion || ''} {t.tlsStatus.kexAlgorithm ? `· ${t.tlsStatus.kexAlgorithm}` : ''}
                      </Typography>
                    </Box>
                  ) : <Typography variant="caption" color="text.secondary">未檢查</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant="outlined" onClick={() => handleCheck(t._id)} disabled={checking === t._id}>
                      {checking === t._id ? '...' : '檢查'}
                    </Button>
                    <Button size="small" onClick={() => startEdit(t)}>編輯</Button>
                    <IconButton size="small" color="error" onClick={() => handleDelete(t._id, t.name)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {targets.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>尚無代理目標，請點擊「新增代理目標」</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Logs */}
      {showLogs && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>代理操作紀錄</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>時間</TableCell>
                <TableCell>動作</TableCell>
                <TableCell>目標</TableCell>
                <TableCell>詳情</TableCell>
                <TableCell>操作者</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log: any) => (
                <TableRow key={log._id}>
                  <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(log.createdAt).toLocaleString('zh-TW')}</TableCell>
                  <TableCell><Chip label={log.action} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{log.targetName || '-'}</TableCell>
                  <TableCell sx={{ fontSize: 11, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.detail || '-'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{log.userEmail || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ── Main Admin Page ────────────────────────────────────────────────────────
export function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <SecurityIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>PQC Navigator — 管理後台</Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>{user?.email}</Typography>
          <Tooltip title="登出">
            <IconButton color="inherit" onClick={logout}><LogoutIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label="會員管理" />
            <Tab label="稽核紀錄" />
            <Tab label="功能管理" />
            <Tab label="代理設定" />
            <Tab label="系統管理" />
            <Tab label="變更密碼" />
          </Tabs>
          {tab === 0 && <UsersTab />}
          {tab === 1 && <AuditTab />}
          {tab === 2 && <FeaturesTab />}
          {tab === 3 && <ProxyTab />}
          {tab === 4 && <SystemTab />}
          {tab === 5 && <PasswordTab />}
        </Paper>
      </Box>
    </Box>
  );
}
